import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ActionIconButton } from '@onesource/components';
import { TProDirectoryRevision } from '@onesource/schemas';
import { formatDistanceToNow } from 'date-fns';
import { FC } from 'react';
import { MdDelete, MdRefresh, MdSave } from 'react-icons/md';

import { statusColor } from './revisions-utils';

export interface RevisionSidebarListProps {
  revisions: TProDirectoryRevision[];
  selectedRevision: TProDirectoryRevision | null;
  isMutating: boolean;
  onSelect: (id: number) => void;
  onResubmit: (revision: TProDirectoryRevision) => void;
  onSaveAsDraft: (revision: TProDirectoryRevision) => void;
  onDelete: (revision: TProDirectoryRevision) => void;
  onSelectCallback?: () => void;
}

export const RevisionSidebarList: FC<RevisionSidebarListProps> = ({
  revisions,
  selectedRevision,
  isMutating,
  onSelect,
  onResubmit,
  onSaveAsDraft,
  onDelete,
  onSelectCallback,
}) => (
  <Stack spacing={0.5}>
    {revisions.map(revision => {
      const isSelected = selectedRevision?.id === revision.id;
      const isPending = revision.status === 'pending';
      const isRejected = revision.status === 'rejected';
      return (
        <Box
          key={revision.id}
          onClick={() => {
            if (!isMutating) {
              onSelect(revision.id);
              onSelectCallback?.();
            }
          }}
          sx={{
            p: 1,
            borderRadius: 1,
            cursor: isMutating ? 'not-allowed' : 'pointer',
            opacity: isMutating ? 0.6 : 1,
            bgcolor: isSelected ? 'action.selected' : 'transparent',
            border: '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            '&:hover': { bgcolor: isMutating ? undefined : 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
            <Chip
              label={revision.status.charAt(0).toUpperCase() + revision.status.slice(1)}
              color={statusColor(revision.status)}
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 0.25 }} onClick={e => e.stopPropagation()}>
              {revision.status !== 'draft' && (
                <ActionIconButton
                  tooltip="Save as draft to edit"
                  icon={<MdSave size={16} />}
                  disabled={isMutating}
                  onClick={() => onSaveAsDraft(revision)}
                />
              )}
              {isRejected && (
                <ActionIconButton
                  tooltip="Resubmit for review"
                  icon={<MdRefresh size={16} />}
                  disabled={isMutating}
                  onClick={() => onResubmit(revision)}
                />
              )}
              {isPending && (
                <ActionIconButton
                  tooltip="Delete"
                  icon={<MdDelete size={16} />}
                  disabled={isMutating}
                  onClick={() => onDelete(revision)}
                />
              )}
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {formatDistanceToNow(new Date(revision.createdAt), { addSuffix: true })}
          </Typography>
        </Box>
      );
    })}
  </Stack>
);
